import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import ACLStore from "lib/aclStore";
import I18nHOC from "UIComponents/I18nHOC";
import UILabel from "UIComponents/UILabel";
import EmptyField from "UIComponents/EmptyField";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import withLoader from "UIComponents/GqlWithLoader";
import UIModal from "UIComponents/UIModal";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import AddNoteDialogue from "UIComponents/AddNoteDialogue";
import classes from "./OrganizationResourcesSelection.scss";
import { AttachmentList, CreateDropDown } from "Attachment";
import SelectResourceDialogue from "./Components/SelectResourceDialogue";
import {
  createOrganizationResources,
  deleteOrganizationResources,
  assessmentOrganizationResourcesReorder,
  updateOrganizationResource,
} from "OrganizationResources/modules/OrganizationResourcesModule";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { getUnitResourcesFeedQuery } from "UnitFlowV3/modules/EntityFlow/Query";
import { getUnitResourcesFeedQuery as learningCourseFlowQueries_getUnitResourcesFeedQuery } from "UnitFlowV3/modules/LearningCourseFlow/Query";
import { getPlannerOrganizationResourcesOfNodeQuery } from "UnitPlans/modules/UnitPlanQuery";
import { getPlannerOrganizationResourcesOfNodeFromCache } from "UnitPlans/modules/UnitPlanGraphqlHelpers";
import { getLabelValue } from "Utils";
import AddAssignment from "ClassRoom/components/AddAssignment";
import classNames from "classnames";
import { getPlannerOrganizationResourcesOfNodeVariables } from "UnitPlans/modules/Utils";
import { getIsLearningCoursesEnabledForUnit } from "UnitPlans/modules/UnitViewModeUtils";
import {
  DeviceDesktopOutlined,
  LinkOutlined,
  PortfolioOutlined,
  GoogleDriveColored,
  OneDriveColored,
  NoteOutlined,
  WarningTriangleOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";
import { Dialog } from "@toddle-edu/ds-web";
import { Trans } from "react-i18next";
import { DELETE } from "Constants/stringConstants";
import { PUSHER_EVENTS, getUnitFlowUtils } from "UnitFlowV3/modules/utils";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";
import { getSelectedCourseId } from "modules/CommonSelectors";

const cardTypes = ["horizontal", "horizontalV2", "verticalHorizontal"];
const NOTES_EDIT_PERMISSIONS = ["NOTES_EDIT_MODE"];
const EMPTY_PERMISSIONS = [];

const DROP_DOWN_OPTIONS = [
  {
    key: "upload_device",
    label: "common:upload_device",
    icon: <DeviceDesktopOutlined size={"xx-small"} />,
  },
  {
    key: "add_link",
    label: "common:add_link",
    icon: <LinkOutlined size={"xx-small"} />,
  },
  {
    key: "add_google_drive",
    label: "common:add_google_drive",
    icon: <GoogleDriveColored size={"xx-small"} />,
  },
  {
    key: "add_one_drive",
    label: "common:add_one_drive",
    icon: <OneDriveColored size={"xx-small"} />,
  },
  {
    key: "add_note",
    label: "common:add_note",
    icon: <NoteOutlined size={"xx-small"} />,
  },
  {
    key: "select_resource",
    label: "common:select_unit_resources",
    icon: <PortfolioOutlined size={"xx-small"} />,
  },
  {
    key: "upload_from_unit_plan",
    label: "upload_from_unit_plan",
    icon: <PortfolioOutlined size={"xx-small"} />,
  },
];

const buttonDropDownContainerStyle = {
  top: 40,
  right: 0,
  left: "initial",
};

const resourceModalData = {
  steps: [
    {
      label: "Select Unit",
      value: "UNIT_SELECTION",
      heading: "Select Unit",
    },
    {
      label: "Select resources",
      value: "RESOURCE_SELECTION",
      heading: "Select Resources",
    },
  ],
};

const styles = {
  containerStyle: {
    gridGap: "40px 24px",
  },
  horizontalCardStyle: {
    padding: "unset",
    border: "unset",
    height: "40px",
    background: "unset",
  },
  attachmentContainerStyle: {
    gridTemplateColumns: "repeat(2,1fr)",
  },
};

const createButtonComponentConfig = {
  icon: "AddCircleOutlined",
  type: "ICON_BUTTON_V2",
  size: "small",
  variant: "plain",
};

const DIALOGS_INFO = {
  DELETE: {
    title: ({ t }) =>
      t("common:delete_with_label", {
        label: t("common:media"),
      }),
    message: ({ t }) => {
      return (
        <Trans i18nKey={"common:delete_msg_with_label_multiline"}>
          {{
            label: t("common:media"),
          }}
          <div></div>
        </Trans>
      );
    },
    confirmButtonText: ({ t }) => t("common:delete_agree"),
    cancelButtonText: ({ t }) => t("common:agree_cancel"),
  },
};

const getAttachmentListStyles = params => {
  const {
    cardType,
    attachments,
    isResourceUploading,
    containerStyle,
    horizontalCardStyle,
    attachmentContainerStyle,
  } = params;
  const applyAttachmentCustomStyles = _.includes(cardTypes, cardType);

  if (!_.isEmpty(attachments) || isResourceUploading) {
    if (applyAttachmentCustomStyles) {
      return {
        containerStyle: styles.containerStyle,
        horizontalCardStyle: styles.horizontalCardStyle,
        attachmentContainerStyle: styles.attachmentContainerStyle,
      };
    } else {
      return {
        containerStyle,
        horizontalCardStyle,
        attachmentContainerStyle,
      };
    }
  } else {
    return {};
  }
};

class OrganizationResourcesSelection extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      selectResourceDialogue: false,
      showAddResourceFromUnitModal: false,
      showFullScreenLoader: false,
      currentDialogAction: "",
      currentNoteDialogItem: {},
      currentNoteDialogAction: "",
    };
  }

  loadMoreResources = event => {
    if (this.props.hasNextPage) {
      this.setState({ showFullScreenLoader: true });
      event?.stopPropagation();
      this.props
        .fetchMoreResources()
        .then(() => {
          this.setState({ showFullScreenLoader: false });
        })
        .catch(error => {
          console.error("Error loading more resources:", error);
          this.setState({ showFullScreenLoader: false });
        });
    }
  };

  toggleSelectUnitResourceDialogue = () => {
    this.setState({
      selectResourceDialogue: !this.state.selectResourceDialogue,
    });
  };

  toggleAddResourceFromUnitModal = () => {
    this.setState({
      showAddResourceFromUnitModal: !this.state.showAddResourceFromUnitModal,
    });
  };

  deleteAssessmentResource = async vals => {
    const {
      deleteOrganizationResources,
      organizationResources,
      parentType,
      parentId,
      portalType,
      variables,
    } = this.props;

    const data = _.differenceBy(organizationResources, vals, "id");

    this.toggleFullScreenLoader();
    await deleteOrganizationResources({
      ids: _.map(data, item => item.id),
      moduleType: parentType,
      moduleId: parentId,
      portalType,
      orderBy: variables?.orderBy,
      filters: variables?.filters,
      orderByDirection: variables?.orderByDirection,
      organizationResourcesFeedQueryVariables: {
        first: null,
      },
    });
    this.toggleFullScreenLoader();
  };

  addSelectedResources = selectedResources => {
    const {
      parentId,
      parentType,
      createOrganizationResources,
      portalType,
      variables,
      organizationResources,
      isLearningCoursesEnabled,
      assessmentType,
    } = this.props;

    const getOrganizationResource = resource => {
      const item = {
        resourceType:
          _.get(_.first(selectedResources), "__typename") ==
          "EntityFlowResource"
            ? "ENTITY_FLOW_RESOURCE"
            : "ORGANIZATION_RESOURCE",
        label: "",
        visibleTo: [],
        type: "PLANNER_RESOURCE",
      };

      if (isLearningCoursesEnabled) {
        const attachment = _.pickBy(
          _.omit(_.get(resource, "attachment", {}), [
            "__typename",
            "id",
            "signedUrl",
          ]),
          value => value != null
        );
        item.attachment = attachment;
        item.label = _.get(resource, "label", "");
      } else {
        item.itemId = resource.id;
      }

      return item;
    };

    createOrganizationResources({
      parentId: null,
      moduleId: parentId,
      moduleType: parentType,
      types: "PLANNER_RESOURCE",
      organizationResources: _.map(selectedResources, getOrganizationResource),
      portalType,
      assessmentType,
      orderBy: variables?.orderBy,
      orderByDirection: variables?.orderByDirection,
      filters: variables?.filters,
      putAfterItemId: _.get(
        _.last(this.getAttachmentsToRender({ organizationResources })),
        "id"
      ),
      organizationResourcesFeedQueryVariables: {
        first: null,
      },
    });
  };

  onContentUploaded = async params => {
    const {
      createOrganizationResources,
      t,
      parentId,
      parentType,
      portalType,
      variables,
      assessmentType,
    } = this.props;

    const organizationResources = [];

    if (_.size(params) > 0) {
      _.forEach(params, param => {
        const attachment = [
          "id",
          "name",
          "title",
          "type",
          "mimeType",
          "url",
          "metadata",
          "thumbUrl",
        ].reduce((result, key) => ({ ...result, [key]: param[key] }), {});
        organizationResources.push({
          label: getLabelValue({ item: param, t }),
          resourceType: "FILE",
          attachment,
        });
      });
    }

    await createOrganizationResources({
      types: "PLANNER_RESOURCE",
      parentId: null,
      visibleTo: [],
      moduleId: parentId,
      moduleType: parentType,
      organizationResources,
      portalType,
      assessmentType,
      orderBy: variables?.orderBy,
      filters: variables?.filters,
      orderByDirection: variables?.orderByDirection,
      putAfterItemId: _.get(
        _.last(
          this.getAttachmentsToRender({
            organizationResources: this.props.organizationResources,
          })
        ),
        "id"
      ),
      organizationResourcesFeedQueryVariables: {
        first: null,
      },
    });
  };

  onDropDownOptionClick = ({ key }) => {
    switch (key) {
      case "select_resource":
        this.toggleSelectUnitResourceDialogue();
        break;
      case "upload_from_unit_plan":
        this.toggleAddResourceFromUnitModal();
        break;
    }
  };

  getAttachmentsToRender = ({ organizationResources }) => {
    return _.reduce(
      organizationResources,
      (result, resource) => {
        const resourceToAdd = _.get(resource, "item", null)
          ? _.get(resource, "item.attachment", null)
          : _.get(resource, "attachment", null);

        if (resourceToAdd) {
          const name =
            _.get(resource, "item.label", null) ||
            _.get(resource, "label", null) ||
            _.get(resourceToAdd, "name");

          result.push({
            ...resourceToAdd,
            id: resource.id,
            resourceId: _.get(resource, "item.id") || _.get(resource, "id"),
            attachmentId: _.get(resource, "item.attachment", null)
              ? _.get(resource, "item.attachment.id", null)
              : _.get(resource, "attachment.id", null),
            name,
          });
        }

        return result;
      },
      []
    );
  };

  getEmptyState = () => {
    // In view mode of unit plan only text should come in empty state
    const { mode, t, showViewEmpty, showEditEmpty } = this.props;
    const showEmpty = mode == "view" ? showViewEmpty : showEditEmpty;
    return showEmpty ? (
      mode == "view" ? (
        <div className={classes.emptyTextContainer}>
          <WarningTriangleOutlined variant="warning" size="xxx-small" />
          <div className={classes.emptyText}>
            {t("common:no_resources_yet")}
          </div>
        </div>
      ) : (
        <EmptyField title={t("common:no_resources_yet")} />
      )
    ) : null;
  };

  getFilteredDropDown = () => {
    const { unitPlanId, assessmentType } = this.props;

    if (unitPlanId) {
      return _.filter(
        DROP_DOWN_OPTIONS,
        item => !_.includes(["upload_from_unit_plan"], item.key)
      );
    } else {
      if (assessmentType === "qt") {
        return _.filter(
          DROP_DOWN_OPTIONS,
          item => !_.includes(["select_resource", "add_note"], item.key)
        );
      }
      return _.filter(
        DROP_DOWN_OPTIONS,
        item =>
          !_.includes(["select_resource", "upload_from_unit_plan"], item.key)
      );
    }
  };

  updateInputFieldLocal = value => {
    const { fieldKey } = this.props;
    this.currentDialgItem = fieldKey ? _.get(value, fieldKey) : value;
    this.setState({ currentDialogAction: DELETE });
  };

  handleDragAndDrop = async params => {
    const {
      assessmentOrganizationResourcesReorder,
      organizationResources,
      variables,
    } = this.props;
    const { destination, source, draggableId } = params;
    const toIndex = destination?.index;
    const fromIndex = source?.index;

    const attachments = this.getAttachmentsToRender({ organizationResources });

    if (_.isEmpty(destination)) {
      return;
    }
    if (
      destination?.droppableId === source?.droppableId &&
      toIndex === fromIndex
    ) {
      return;
    }

    let putAfterItemId = null;
    const id = attachments[fromIndex].id;

    if (toIndex > 0) {
      if (fromIndex < toIndex) {
        putAfterItemId = _.get(attachments, `${[toIndex]}.id`, null);
      } else {
        putAfterItemId = _.get(attachments, `${[toIndex - 1]}.id`, null);
      }
    } else if (toIndex == attachments.length - 1) {
      putAfterItemId = _.get(attachments.last(), `id`, null);
    }

    await assessmentOrganizationResourcesReorder({
      id,
      putAfterItemId,
      variables,
      toIndex,
      fromIndex,
    });
  };

  closeNoteModal = () => {
    this.setState({ currentNoteDialogItem: {}, currentNoteDialogAction: "" });
  };

  onEditNote = attachment => {
    this.setState({
      currentNoteDialogItem: {
        title: _.get(attachment, "name", ""),
        itemId: _.get(attachment, "resourceId") || _.get(attachment, "id", ""),
      },
      currentNoteDialogAction: "EDIT",
    });
  };

  saveNote = async params => {
    const { note } = params;
    const { updateOrganizationResource, portalType } = this.props;
    const { currentNoteDialogItem } = this.state;

    await updateOrganizationResource({
      id: _.get(currentNoteDialogItem, "itemId", ""),
      label: note,
      attachment: {
        name: note,
        title: note,
        type: "NOTE",
      },
      portalType,
    });
  };

  getExtraDropdownOptions = ({ attachment }) => {
    const { t, mode } = this.props;
    const isNote = _.get(attachment, "type") === "NOTE";
    const isEditMode = mode === "edit";

    if (isNote && isEditMode) {
      return [
        {
          key: "edit_note",
          label: t("common:edit"),
          icon: <PencilLineOutlined size="xx-small" />,
          isVisible: true,
          onClick: () => this.onEditNote(attachment),
        },
      ];
    }
    return [];
  };

  getAttachmentList = () => {
    const {
      organizationResources,
      mode,
      cardType,
      parentId,
      cardStyle,
      labelComponent,
      label,
      toolTipText,
      isDisabledModeObj,
      resourcesLabelContainerStyle,
      isResourceUploading,
      isLoading,
      showLoader,
      iconStyles,
      portalType,
      isDragAndDropEnabled,
      fieldKey,
      isToddleLibrary,
      subscribeToddleLibraryEvents,
      unSubscribeToddleLibraryEvents,
      totalAttachmentsCount,
      testId,
      dsVersion,
      parentType,
    } = this.props;

    const attachments = this.getAttachmentsToRender({ organizationResources });

    const {
      containerStyle = {},
      horizontalCardStyle = {},
      attachmentContainerStyle = {},
    } = getAttachmentListStyles({
      cardType,
      attachments,
      isResourceUploading,
      containerStyle: this.props.containerStyle,
      horizontalCardStyle: this.props.horizontalCardStyle,
      attachmentContainerStyle: this.props.attachmentContainerStyle,
    });

    return (
      <>
        {isLoading && showLoader && <FullScreenLoader />}
        <AttachmentList
          name={fieldKey}
          value={attachments}
          cardType={cardType}
          mode={mode}
          showCreateDropdown={false}
          clubbingCards={
            _.isEqual(parentType, "UNIT_PLAN") ? _.size(attachments) : 27
          }
          showSingleCardTheme={false}
          parentId={parentId}
          parentType={"ORG_RESOURCES"}
          cardStyle={cardStyle}
          labelComponent={
            _.get(labelComponent, "props.label") !== label && labelComponent
              ? labelComponent
              : null
          }
          labelContainerStyle={resourcesLabelContainerStyle}
          updateInputField={this.updateInputFieldLocal}
          containerStyle={containerStyle}
          horizontalCardStyle={horizontalCardStyle}
          attachmentContainerStyle={attachmentContainerStyle}
          toolTipText={toolTipText}
          isDisabledModeObj={isDisabledModeObj}
          iconStyles={iconStyles}
          portalType={portalType}
          isDragAndDropEnabled={isDragAndDropEnabled}
          customHandleDragAndDrop={this.handleDragAndDrop}
          isToddleLibrary={isToddleLibrary}
          subscribeToddleLibraryEvents={subscribeToddleLibraryEvents}
          unSubscribeToddleLibraryEvents={unSubscribeToddleLibraryEvents}
          isPrintEnabled={
            !ACLStore.can("FeatureFlag:PreventPrintAndScreenshots")
          }
          totalAttachmentsCount={totalAttachmentsCount}
          testId={testId}
          dsVersion={dsVersion}
          loadMoreResources={this.loadMoreResources}
          getExtraDropdownOptions={this.getExtraDropdownOptions}
          onEditAttachmentClick={this.onEditNote}
          allowedPermissions={
            mode === "edit" ? NOTES_EDIT_PERMISSIONS : EMPTY_PERMISSIONS
          }
        />
      </>
    );
  };

  onDialogClose = () => {
    this.setState({ currentDialogAction: "" });
  };

  onDialogConfirm = () => {
    switch (this.state.currentDialogAction) {
      case DELETE:
        this.deleteAssessmentResource(this.currentDialgItem);
        break;
    }

    this.onDialogClose();
  };

  toggleFullScreenLoader = () => {
    this.setState(prevState => {
      return {
        ...prevState,
        showFullScreenLoader: !prevState.showFullScreenLoader,
      };
    });
  };

  render() {
    const {
      t,
      unitPlanId,
      parentId,
      parentType,
      label,
      mode,
      nodeId,
      ResourceslabelContainerStyle,
      isShowIconsInBar,
      showV2Icons,
      topContainerStyle = {},
      labelComponent,
      resourcesContainerStyle = {},
      labelContainerStyle = {},
      destroyPopupOnHide,
      getNewDropdownPopupContainer,
      testId,
      dsVersion,
    } = this.props;

    const {
      selectResourceDialogue,
      showAddResourceFromUnitModal,
      currentDialogAction,
      showFullScreenLoader,
      currentNoteDialogAction,
      currentNoteDialogItem,
    } = this.state;

    const topContainerClass = classNames({
      [classes.topContainer]: true,
      [classes.noLabelTopContainer]: _.isEmpty(label),
    });

    return (
      <div className={classes.container} style={resourcesContainerStyle}>
        <div
          className={classes.labelContainer}
          style={ResourceslabelContainerStyle}
        >
          <UILabel
            labelStyle={{ margin: 0 }}
            label={label}
            labelComponent={labelComponent}
            labelContainerStyle={labelContainerStyle}
          />

          {mode == "edit" && (
            <div className={topContainerClass} style={topContainerStyle}>
              <CreateDropDown
                dropDownOptions={this.getFilteredDropDown()}
                buttonDropDownContainerStyle={buttonDropDownContainerStyle}
                exportAsPdf={true}
                parentType={"ORG_RESOURCES"}
                parentId={nodeId}
                onMultipleFilesUploaded={this.onContentUploaded}
                onDropDownOptionClick={this.onDropDownOptionClick}
                isShowIconsInBar={isShowIconsInBar}
                showV2Icons={showV2Icons}
                testId={`${testId}-addResources`}
                isNewDropdown={true}
                createButtonComponentConfig={createButtonComponentConfig}
                destroyPopupOnHide={destroyPopupOnHide}
                getNewDropdownPopupContainer={getNewDropdownPopupContainer}
                dsVersion={dsVersion}
              />
            </div>
          )}
        </div>
        {this.getAttachmentList()}

        {selectResourceDialogue && (
          <SelectResourceDialogue
            parentType={parentType}
            parentId={parentId}
            unitPlanId={unitPlanId}
            testId={testId}
            onClose={this.toggleSelectUnitResourceDialogue}
            addSelectedResources={this.addSelectedResources}
          />
        )}

        {showAddResourceFromUnitModal && (
          <UIModal
            isOpen={showAddResourceFromUnitModal}
            modalContent={classes.addResourceModalContainer}
            onRequestClose={() => this.toggleAddResourceFromUnitModal()}
          >
            <AddAssignment
              mode={"create"}
              resourceSelection={true}
              steps={resourceModalData["steps"]}
              onClose={() => this.toggleAddResourceFromUnitModal()}
              onDoneResourceSelection={params => {
                this.onContentUploaded(params);
              }}
            ></AddAssignment>
          </UIModal>
        )}

        {!_.isEmpty(currentDialogAction) ? (
          <Dialog
            isOpen={true}
            variant={"delete-dialog"}
            title={DIALOGS_INFO[currentDialogAction].title({ t })}
            onClose={this.onDialogClose}
            buttonOneText={DIALOGS_INFO[currentDialogAction].cancelButtonText({
              t,
            })}
            buttonTwoText={DIALOGS_INFO[currentDialogAction].confirmButtonText({
              t,
            })}
            onButtonOneClick={this.onDialogClose}
            onButtonTwoClick={this.onDialogConfirm}
            message={DIALOGS_INFO[currentDialogAction].message({ t })}
          />
        ) : null}

        {!!currentNoteDialogAction && (
          <AddNoteDialogue
            note={currentNoteDialogItem}
            mode={mode === "view" ? "VIEW_ONLY" : currentNoteDialogAction}
            onClose={this.closeNoteModal}
            dsVersion={dsVersion || "2.0"}
            saveNote={this.saveNote}
          />
        )}

        {showFullScreenLoader ? <FullScreenLoader /> : null}
      </div>
    );
  }
}

OrganizationResourcesSelection.propTypes = {
  ...UIBaseComponent.propTypes,
  // Pagination props
  hasNextPage: PropTypes.bool,
  fetchMoreResources: PropTypes.func,
  pageInfo: PropTypes.object,
  afterCursor: PropTypes.string,
  pageSize: PropTypes.number,
};

OrganizationResourcesSelection.defaultProps = {
  ...UIBaseComponent.defaultProps,
  mode: "edit",
  cardType: "verticalHorizontal",
  cardStyle: {
    height: 232,
  },
  testId: "IBComponents-organizationResources",
  hasNextPage: false,
  fetchMoreResources: () => {},
  pageSize: 25,
};

const mapActionCreators = {
  createOrganizationResources,
  deleteOrganizationResources,
  assessmentOrganizationResourcesReorder,
  updateOrganizationResource,
};

const mapStateToProps = (state, ownProps) => {
  const { parentId, parentType, portalType } = ownProps;
  const progressOfUploads = state.app_services.progressOfUploads;
  const { getUnitResourcesVariablesMemoized } = getUnitFlowUtils();

  const variables = getPlannerOrganizationResourcesOfNodeVariables({
    parentId,
    parentType,
    portalType,
  });
  const keys = Object.keys(progressOfUploads);
  const isResourceUploading = _.some(keys, key => {
    const resource = progressOfUploads[key];
    return resource?.parentType == "ORG_RESOURCES";
  });

  const learningCourseId =
    _.get(state, "planner.unitPlanData.learningCourseId", null) ||
    getSelectedLearningCourseId(state);
  const classId = getSelectedCourseId(state);

  const unitResourcesQueryVariables = getUnitResourcesVariablesMemoized({
    parentId,
    learningCourseId,
    classId,
    first: _.get(ownProps, "pageSize", 25),
    after: _.get(ownProps, "afterCursor", null),
  });

  const isLearningCoursesEnabled = getIsLearningCoursesEnabledForUnit();

  return {
    nodeId: parentId,
    type: parentType,
    organizationResources: ownProps.value,
    isData: !_.isEmpty(ownProps.value),
    isLearningCoursesEnabled,
    isResourceUploading,
    showLoader: _.get(ownProps, "showLoader", false),
    variables,
    unitResourcesQueryVariables,
  };
};

OrganizationResourcesSelection.propTypes = {
  ...UIBaseComponent.propTypes,
};
OrganizationResourcesSelection.defaultProps = {
  ...UIBaseComponent.defaultProps,
  mode: "edit",
  cardType: "vertical",
  showViewEmpty: true,
  pageSize: 25,
};

const getUnitResourcesFeedQueryFunc = (query, isEntityFlow) =>
  graphql(query, {
    name: "getUnitResourcesFeed",
    skip: ({
      value,
      nodeId,
      renderEntityFlowResources,
      isLearningCoursesEnabled,
    }) => {
      return (
        value ||
        !nodeId ||
        !renderEntityFlowResources ||
        (isEntityFlow ? isLearningCoursesEnabled : !isLearningCoursesEnabled)
      );
    },
    options: ({ unitResourcesQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: unitResourcesQueryVariables,
    }),
    props({ getUnitResourcesFeed, ownProps: { onPusherEventTriggered } }) {
      const unitResourcesData = getUnitFlowUtils().getUnitResourcesFeed(
        getUnitResourcesFeed
      );

      const unitResourcesEdges = _.get(unitResourcesData, "edges", []);
      const unitResources = _.map(unitResourcesEdges, "node");
      const totalAttachmentsCount = _.get(
        unitResourcesData,
        "resourceBasedCount.totalCount",
        0
      );
      const pageInfo = _.get(unitResourcesData, "pageInfo", {});
      const { hasNextPage, endCursor } = pageInfo;

      return {
        organizationResources: unitResources,
        totalAttachmentsCount,
        pageInfo,
        hasNextPage,
        fetchMoreResources: () => {
          if (hasNextPage) {
            return getUnitResourcesFeed.fetchMore({
              variables: {
                after: endCursor,
              },
              updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;

                // Determine which path to use based on what's available
                const isLearningCourse = _.has(
                  prev,
                  "node.learningCourseFlowFeedWrapper"
                );

                let prevEdges = [];
                let newEdges = [];

                if (isLearningCourse) {
                  prevEdges = _.get(
                    prev,
                    "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                    []
                  );
                  newEdges = _.get(
                    fetchMoreResult,
                    "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                    []
                  );

                  return {
                    ...prev,
                    node: {
                      ...prev.node,
                      learningCourseFlowFeedWrapper: {
                        ...prev.node.learningCourseFlowFeedWrapper,
                        learningCourseFlowFeed: {
                          ...prev.node.learningCourseFlowFeedWrapper
                            .learningCourseFlowFeed,
                          edges: [...prevEdges, ...newEdges],
                          pageInfo:
                            fetchMoreResult.node.learningCourseFlowFeedWrapper
                              .learningCourseFlowFeed.pageInfo,
                        },
                      },
                    },
                  };
                } else {
                  prevEdges = _.get(
                    prev,
                    "node.unitFlowFeed.unitFlowFlatFeed.edges",
                    []
                  );
                  newEdges = _.get(
                    fetchMoreResult,
                    "node.unitFlowFeed.unitFlowFlatFeed.edges",
                    []
                  );

                  return {
                    ...prev,
                    node: {
                      ...prev.node,
                      unitFlowFeed: {
                        ...prev.node.unitFlowFeed,
                        unitFlowFlatFeed: {
                          ...prev.node.unitFlowFeed.unitFlowFlatFeed,
                          edges: [...prevEdges, ...newEdges],
                          pageInfo:
                            fetchMoreResult.node.unitFlowFeed.unitFlowFlatFeed
                              .pageInfo,
                        },
                      },
                    },
                  };
                }
              },
            });
          }
          return Promise.resolve();
        },
        onPusherEventTriggered: (params = {}) => {
          const { event, isUnitResourceRefetch } = params;

          onPusherEventTriggered?.(params);

          if (event === PUSHER_EVENTS.FEED_UPDATE && isUnitResourceRefetch) {
            getUnitResourcesFeed.refetch();
          }
        },
        isData: !_.isEmpty(getUnitResourcesFeed),
        isLoading: [1, 2, 3].includes(getUnitResourcesFeed.networkStatus), // Include 3 for fetchMore loading state
      };
    },
  });

export default compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getPlannerOrganizationResourcesOfNodeQuery, {
    name: "getPlannerOrganizationResourcesOfNode",
    skip: ({ value, nodeId, renderEntityFlowResources }) =>
      value || !nodeId || renderEntityFlowResources,
    options: ({ variables }) => ({
      fetchPolicy: "cache-and-network",
      variables,
    }),
    props({ getPlannerOrganizationResourcesOfNode }) {
      const nodeDetails = getPlannerOrganizationResourcesOfNodeFromCache(
        getPlannerOrganizationResourcesOfNode.variables
      );

      return {
        onPusherEventTriggered: (params = {}) => {
          const { event } = params;
          if (event == "assessmentResourcesUpdated") {
            getPlannerOrganizationResourcesOfNode.refetch();
          }
        },
        organizationResources: _.map(
          _.get(nodeDetails, "organizationResourcesV2.edges", []),
          "node"
        ),
        isData: !_.isEmpty(nodeDetails),
        isLoading:
          getPlannerOrganizationResourcesOfNode["networkStatus"] == 1 ||
          getPlannerOrganizationResourcesOfNode["networkStatus"] == 2,
      };
    },
  }),
  getUnitResourcesFeedQueryFunc(getUnitResourcesFeedQuery, true),
  getUnitResourcesFeedQueryFunc(
    learningCourseFlowQueries_getUnitResourcesFeedQuery,
    false
  ),
  withPusherBind([PUSHER_EVENTS.FEED_UPDATE, "assessmentResourcesUpdated"]),
  withLoader
)(OrganizationResourcesSelection);
